import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, CheckCircle, Linkedin, Sun, Moon, Target, Users,
  Calendar, MessageSquare, LayoutTemplate, Zap, Link2, FileText
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { API_URL } from '../lib/utils';

export default function LoginPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLinkedInLogin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/linkedin/login-url`);
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        alert('LinkedIn login is not configured. Please use Google login.');
      }
    } catch {
      alert('LinkedIn login is not available. Please use Google login.');
    }
  };

  return (
    <div className="min-h-screen bg-main">
      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-card border border-border text-muted hover:text-foreground transition-colors"
          data-testid="theme-toggle"
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-indigo-500/5" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-3 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary via-blue-500 to-indigo-600 flex items-center justify-center shadow-xl shadow-primary/25">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-4xl font-bold text-foreground">PostedIn</h1>
            </div>

            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Your LinkedIn,{' '}
              <span className="relative">
                <span className="gradient-text">10x More Powerful</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 8C50 4 100 2 150 6C200 10 250 4 298 8" stroke="url(#underline-gradient)" strokeWidth="4" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="underline-gradient" x1="0" y1="0" x2="300" y2="0">
                      <stop stopColor="#0EA5E9"/>
                      <stop offset="1" stopColor="#8B5CF6"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-muted mb-4 max-w-3xl mx-auto leading-relaxed">
              Whether you want to <span className="text-foreground font-medium">get more clients</span>, 
              <span className="text-foreground font-medium"> grow your network</span>, or 
              <span className="text-foreground font-medium"> become a thought leader</span>
            </p>
            <p className="text-lg text-muted mb-10 max-w-2xl mx-auto">
              PostedIn helps you create content that gets noticed, builds trust, and opens doors.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 mb-12 max-w-md mx-auto">
              <motion.button
                onClick={handleLogin}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full bg-white hover:bg-gray-50 text-gray-800 font-medium text-base px-6 py-3.5 rounded-lg border border-gray-300 shadow-sm hover:shadow flex items-center justify-center gap-3 transition-all"
                data-testid="login-google-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google</span>
              </motion.button>
              
              <motion.button
                onClick={handleLinkedInLogin}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full bg-[#0A66C2] hover:bg-[#004182] text-white font-medium text-base px-6 py-3.5 rounded-lg shadow-sm hover:shadow flex items-center justify-center gap-3 transition-all"
                data-testid="login-linkedin-btn"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span>LinkedIn</span>
              </motion.button>

              <div className="relative w-full my-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-main px-2 text-muted">Get Started Free</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Free to start</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-20 bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Built for Every LinkedIn User
            </h3>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              No matter your goal, PostedIn helps you show up consistently and make an impact.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-blue-500/20 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <h4 className="font-heading text-xl font-bold text-foreground mb-3">Entrepreneurs & Founders</h4>
                <p className="text-muted mb-4">Turn your expertise into clients. Generate leads while you sleep with content that sells without being salesy.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">Lead Generation</span>
                  <span className="px-3 py-1 text-xs font-medium bg-emerald-500/10 text-emerald-500 rounded-full">7-Day Sprints</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-card border border-border rounded-2xl p-8 hover:border-amber-500/50 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-amber-500" />
                </div>
                <h4 className="font-heading text-xl font-bold text-foreground mb-3">Career Professionals</h4>
                <p className="text-muted mb-4">Get noticed by recruiters and decision-makers. Build a personal brand that opens doors to your next opportunity.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs font-medium bg-amber-500/10 text-amber-500 rounded-full">Networking</span>
                  <span className="px-3 py-1 text-xs font-medium bg-orange-500/10 text-orange-500 rounded-full">Visibility</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-card border border-border rounded-2xl p-8 hover:border-indigo-500/50 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-6">
                  <Sparkles className="w-7 h-7 text-indigo-500" />
                </div>
                <h4 className="font-heading text-xl font-bold text-foreground mb-3">Thought Leaders</h4>
                <p className="text-muted mb-4">Share ideas that matter. Spark conversations, grow your influence, and become the go-to voice in your industry.</p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-xs font-medium bg-indigo-500/10 text-indigo-500 rounded-full">Engagement</span>
                  <span className="px-3 py-1 text-xs font-medium bg-purple-500/10 text-purple-500 rounded-full">Influence</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Win on LinkedIn
            </h3>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Stop staring at a blank screen. Let AI help you create content that actually gets results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-card border border-border rounded-xl p-6 hover:border-primary/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <h4 className="font-heading font-bold text-foreground mb-2">7-Day Content Sprints</h4>
              <p className="text-sm text-muted">Get a full week of engaging posts with hooks, stories, and CTAs. Never run out of ideas again.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-card border border-border rounded-xl p-6 hover:border-amber-500/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-amber-500" />
              </div>
              <h4 className="font-heading font-bold text-foreground mb-2">Comment Generator</h4>
              <p className="text-sm text-muted">Engage meaningfully on others' posts. Build relationships that turn into opportunities.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-card border border-border rounded-xl p-6 hover:border-indigo-500/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-4">
                <LayoutTemplate className="w-6 h-6 text-indigo-500" />
              </div>
              <h4 className="font-heading font-bold text-foreground mb-2">15 Pro Templates</h4>
              <p className="text-sm text-muted">Templates for coaches, founders, creators, and more. Pick one and start creating in seconds.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="bg-card border border-border rounded-xl p-6 hover:border-emerald-500/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-emerald-500" />
              </div>
              <h4 className="font-heading font-bold text-foreground mb-2">A/B Hook Testing</h4>
              <p className="text-sm text-muted">5 different hooks for every post. Find what resonates with your audience and 10x your engagement.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="bg-card border border-border rounded-xl p-6 hover:border-rose-500/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-rose-500/10 flex items-center justify-center mb-4">
                <Link2 className="w-6 h-6 text-rose-500" />
              </div>
              <h4 className="font-heading font-bold text-foreground mb-2">Import from Any URL</h4>
              <p className="text-sm text-muted">Turn articles, tweets, and videos into LinkedIn content. Repurpose what's already working.</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }} className="bg-card border border-border rounded-xl p-6 hover:border-[#0077b5]/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-[#0077b5]/10 flex items-center justify-center mb-4">
                <Linkedin className="w-6 h-6 text-[#0077b5]" />
              </div>
              <h4 className="font-heading font-bold text-foreground mb-2">Direct to LinkedIn</h4>
              <p className="text-sm text-muted">Schedule and post directly to LinkedIn. No copy-paste, no hassle. One click publishing.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-primary/5 via-card to-indigo-500/5 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">15+</div>
              <div className="text-sm text-muted">Pro Templates</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">7</div>
              <div className="text-sm text-muted">Days of Content</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">5x</div>
              <div className="text-sm text-muted">Hook Variants</div>
            </div>
            <div>
              <div className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-2">$19</div>
              <div className="text-sm text-muted">Per Month Pro</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to Transform Your LinkedIn Presence?
            </h3>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Join thousands of professionals who've stopped struggling with content and started seeing real results.
            </p>
            
            <motion.button
              onClick={handleLogin}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-600/90 text-white text-lg px-10 py-5 rounded-xl flex items-center justify-center gap-3 mx-auto shadow-xl shadow-primary/25 transition-all"
            >
              <Sparkles className="w-5 h-5" />
              Start Creating for Free
            </motion.button>
            
            <p className="text-sm text-muted mt-4">
              Free plan includes 2 sprints per month. No credit card required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-foreground">PostedIn</span>
            </div>
            <p className="text-sm text-muted">
              &copy; 2026 PostedIn. Built for professionals who want to stand out.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
